/**
 * Project Compliance Configuration Component
 *
 * Manages compliance requirements for a project
 * Configures GDPR, CCPA, SOC2, HIPAA, and other compliance needs
 *
 * Features:
 * - Enable/disable compliance requirements
 * - Configure compliance-specific settings
 * - Inherit from client-level settings
 * - Save compliance configuration to Project.Metadata
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Checkbox, Alert, Spinner, Label, Select, Textarea } from 'flowbite-react';
import { HiCheckCircle, HiShieldCheck, HiSave } from 'react-icons/hi';

import { getProject, updateProject, type Project } from '../api/projects';

interface ProjectComplianceConfigProps {
  clientId: string;
  companyId: string;
  projectId: string;
  onUpdate?: () => void;
}

interface ComplianceSettings {
  gdpr_enabled: boolean;
  ccpa_enabled: boolean;
  soc2_enabled: boolean;
  hipaa_enabled: boolean;
  pci_dss_enabled: boolean;
  data_retention_days?: number;
  data_residency?: string;
  encryption_required: boolean;
  audit_logging_required: boolean;
  notes?: string;
}

const DEFAULT_COMPLIANCE: ComplianceSettings = {
  gdpr_enabled: false,
  ccpa_enabled: false,
  soc2_enabled: false,
  hipaa_enabled: false,
  pci_dss_enabled: false,
  encryption_required: true,
  audit_logging_required: true
};

export const ProjectComplianceConfig: React.FC<ProjectComplianceConfigProps> = ({
  clientId,
  companyId,
  projectId,
  onUpdate
}) => {
  const [complianceSettings, setComplianceSettings] = useState<ComplianceSettings>(DEFAULT_COMPLIANCE);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadComplianceSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getProject(clientId, companyId, projectId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to load project');
      }

      const project = response.data.Records[0];

      // Extract compliance settings from metadata
      if (project.Metadata && project.Metadata.compliance) {
        setComplianceSettings({
          ...DEFAULT_COMPLIANCE,
          ...project.Metadata.compliance
        });
      } else {
        setComplianceSettings(DEFAULT_COMPLIANCE);
      }
    } catch (err) {
      console.error('[ProjectComplianceConfig] Error loading compliance settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load compliance settings');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, companyId, projectId]);

  useEffect(() => {
    loadComplianceSettings();
  }, [loadComplianceSettings]);

  const handleToggle = useCallback((field: keyof ComplianceSettings) => {
    setComplianceSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  const handleChange = useCallback((field: keyof ComplianceSettings, value: any) => {
    setComplianceSettings(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Get current project to preserve other metadata
      const projectResponse = await getProject(clientId, companyId, projectId);
      if (!projectResponse.success) {
        throw new Error('Failed to load current project');
      }

      const currentProject = projectResponse.data.Records[0];
      const currentMetadata = currentProject.Metadata || {};

      // Update metadata with compliance settings
      const updatedMetadata = {
        ...currentMetadata,
        compliance: complianceSettings
      };

      const response = await updateProject(clientId, companyId, projectId, {
        Metadata: updatedMetadata
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to save compliance settings');
      }

      setSuccessMessage('Compliance settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      onUpdate?.();
    } catch (err) {
      console.error('[ProjectComplianceConfig] Error saving compliance settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save compliance settings');
    } finally {
      setIsSaving(false);
    }
  }, [clientId, companyId, projectId, complianceSettings, onUpdate]);

  const getActiveComplianceCount = () => {
    return [
      complianceSettings.gdpr_enabled,
      complianceSettings.ccpa_enabled,
      complianceSettings.soc2_enabled,
      complianceSettings.hipaa_enabled,
      complianceSettings.pci_dss_enabled
    ].filter(Boolean).length;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" icon={HiCheckCircle} onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-600">
            {getActiveComplianceCount()} compliance frameworks enabled
          </p>
        </div>
        <Button
          color="blue"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            <>
              <HiSave className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Compliance Frameworks */}
      <Card>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <HiShieldCheck className="w-5 h-5 mr-2 text-blue-600" />
          Compliance Frameworks
        </h4>

        <div className="space-y-4">
          {/* GDPR */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label htmlFor="gdpr" className="flex items-start space-x-3 cursor-pointer">
              <Checkbox
                id="gdpr"
                checked={complianceSettings.gdpr_enabled}
                onChange={() => handleToggle('gdpr_enabled')}
              />
              <div>
                <span className="font-medium">GDPR (General Data Protection Regulation)</span>
                <p className="text-xs text-gray-600 mt-1">
                  EU data protection and privacy regulation. Requires data subject rights,
                  consent management, and breach notification.
                </p>
              </div>
            </label>
          </div>

          {/* CCPA */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label htmlFor="ccpa" className="flex items-start space-x-3 cursor-pointer">
              <Checkbox
                id="ccpa"
                checked={complianceSettings.ccpa_enabled}
                onChange={() => handleToggle('ccpa_enabled')}
              />
              <div>
                <span className="font-medium">CCPA (California Consumer Privacy Act)</span>
                <p className="text-xs text-gray-600 mt-1">
                  California privacy law. Requires consumer rights to access, delete,
                  and opt-out of data sale.
                </p>
              </div>
            </label>
          </div>

          {/* SOC 2 */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label htmlFor="soc2" className="flex items-start space-x-3 cursor-pointer">
              <Checkbox
                id="soc2"
                checked={complianceSettings.soc2_enabled}
                onChange={() => handleToggle('soc2_enabled')}
              />
              <div>
                <span className="font-medium">SOC 2 (Service Organization Control)</span>
                <p className="text-xs text-gray-600 mt-1">
                  Security, availability, processing integrity, confidentiality, and privacy
                  controls for service organizations.
                </p>
              </div>
            </label>
          </div>

          {/* HIPAA */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label htmlFor="hipaa" className="flex items-start space-x-3 cursor-pointer">
              <Checkbox
                id="hipaa"
                checked={complianceSettings.hipaa_enabled}
                onChange={() => handleToggle('hipaa_enabled')}
              />
              <div>
                <span className="font-medium">HIPAA (Health Insurance Portability and Accountability Act)</span>
                <p className="text-xs text-gray-600 mt-1">
                  Healthcare data protection in the United States. Requires safeguards
                  for protected health information (PHI).
                </p>
              </div>
            </label>
          </div>

          {/* PCI DSS */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label htmlFor="pci_dss" className="flex items-start space-x-3 cursor-pointer">
              <Checkbox
                id="pci_dss"
                checked={complianceSettings.pci_dss_enabled}
                onChange={() => handleToggle('pci_dss_enabled')}
              />
              <div>
                <span className="font-medium">PCI DSS (Payment Card Industry Data Security Standard)</span>
                <p className="text-xs text-gray-600 mt-1">
                  Security standards for organizations that handle credit card data.
                  Requires secure storage, transmission, and processing.
                </p>
              </div>
            </label>
          </div>
        </div>
      </Card>

      {/* Security Requirements */}
      <Card>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Security Requirements
        </h4>

        <div className="space-y-4">
          <label htmlFor="encryption" className="flex items-center space-x-2 cursor-pointer">
            <Checkbox
              id="encryption"
              checked={complianceSettings.encryption_required}
              onChange={() => handleToggle('encryption_required')}
            />
            <span>Encryption required for data at rest and in transit</span>
          </label>

          <label htmlFor="audit_logging" className="flex items-center space-x-2 cursor-pointer">
            <Checkbox
              id="audit_logging"
              checked={complianceSettings.audit_logging_required}
              onChange={() => handleToggle('audit_logging_required')}
            />
            <span>Comprehensive audit logging required</span>
          </label>
        </div>
      </Card>

      {/* Data Governance */}
      <Card>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Data Governance
        </h4>

        <div className="space-y-4">
          <div>
            <Label htmlFor="retention_days" value="Data Retention Period (days)" />
            <input
              id="retention_days"
              type="number"
              min="1"
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
              value={complianceSettings.data_retention_days || ''}
              onChange={(e) => handleChange('data_retention_days', parseInt(e.target.value) || undefined)}
              placeholder="e.g., 365"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of days to retain data before automatic deletion
            </p>
          </div>

          <div>
            <Label htmlFor="data_residency" value="Data Residency" />
            <Select
              id="data_residency"
              value={complianceSettings.data_residency || ''}
              onChange={(e) => handleChange('data_residency', e.target.value || undefined)}
            >
              <option value="">Not specified</option>
              <option value="us">United States</option>
              <option value="eu">European Union</option>
              <option value="uk">United Kingdom</option>
              <option value="ca">Canada</option>
              <option value="au">Australia</option>
              <option value="global">Global</option>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Geographic location where data must be stored
            </p>
          </div>

          <div>
            <Label htmlFor="compliance_notes" value="Compliance Notes" />
            <Textarea
              id="compliance_notes"
              rows={3}
              value={complianceSettings.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value || undefined)}
              placeholder="Additional compliance requirements, special handling instructions, etc."
            />
          </div>
        </div>
      </Card>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs text-gray-700">
          <strong>Note:</strong> Compliance settings affect code generation, security scanning,
          and documentation requirements. Ensure your infrastructure and processes align with
          selected compliance frameworks.
        </p>
      </div>
    </div>
  );
};

export default ProjectComplianceConfig;
